import axios from "axios";
import { getAccessToken, clearAccessToken } from "../common";
import { toast } from "react-toastify";

const TOAST_IDS = {
  timeout: "api-timeout",
  offline: "api-offline",
  network: "api-network",
};

const apiClient = axios.create({
  baseURL:
    process.env.REACT_APP_API_BASE_URL ||
    "https://pms-api-development-1019281934685.asia-south1.run.app/",
  // Default request timeout so we can distinguish "timeout" vs generic network error.
  // Individual requests can override this via config.timeout.
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.config?.skipErrorToast) {
      return Promise.reject(error);
    }

    if (error?.response) {
      const status = error.response.status;

      if (status === 401) {
        clearAccessToken();
        localStorage.clear();
        toast.error("Session expired. Please login again.");
        window.location.href = "/";
        return Promise.reject(error);
      }

      if (status === 403) {
        const errorMessage = error?.response?.data?.message || "You don't have permission to access this resource.";
        toast.error(errorMessage);
        return Promise.reject(error);
      }

      if (error?.response?.data?.message) {
        console.error(error.response.data.message);
        toast.error(error.response.data.message);
      }
    } else if (error?.code === "ECONNABORTED") {
      toast.error("Request timed out. Server took too long to respond.", {
        toastId: TOAST_IDS.timeout,
      });
    } else if (typeof navigator !== "undefined" && navigator.onLine === false) {
      toast.error("You are offline. Please check your internet connection.", {
        toastId: TOAST_IDS.offline,
      });
    } else if (error?.message === "Network Error") {
      toast.error("Cannot reach server (network/CORS). Please try again.", {
        toastId: TOAST_IDS.network,
      });
    } else {
      console.error("An error occurred. Please try again later.");
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  get: async (url, params = {}, config = {}) => {
    try {
      const response = await apiClient.get(url, { params, ...config });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      return response;
    } catch (error) {
      throw error;
    }
  },

  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (url, params = {}, config = {}) => {
    try {
      const response = await apiClient.delete(url, { params, ...config });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default apiClient;
